import { useEffect, useRef, useState, useCallback } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Loader2,
  Radio,
} from "lucide-react";

/**
 * YouTube Push Player with Fallback Support
 * Auto-embeds a YouTube channel's live stream, falls back to a video if not live
 */
export const YouTubePushPlayer = ({
  youtubeChannelId,
  fallbackVideoUrl,
  autoPlay = true,
  onReady,
  onLiveStatusChange,
}) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  const hideControlsTimeout = useRef(null);
  const checkLiveTimeout = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(100);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [playerReady, setPlayerReady] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  // Extract video ID from fallback URL
  const extractVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const fallbackVideoId = extractVideoId(fallbackVideoUrl);

  // Switch to fallback video
  const switchToFallback = useCallback(() => {
    if (!usingFallback) {
      console.log("[YouTubePush] Switching to fallback video");
      setUsingFallback(true);
      setIsLoading(true);
      setPlayerReady(false);
      onLiveStatusChange?.(false);
    }
  }, [usingFallback, onLiveStatusChange]);

  // Load YouTube IFrame API and create player
  useEffect(() => {
    if (!youtubeChannelId && !fallbackVideoId) return;

    // Load the IFrame Player API code asynchronously
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    const initPlayer = () => {
      if (window.YT && window.YT.Player && iframeRef.current) {
        if (playerRef.current) {
          try {
            playerRef.current.destroy();
          } catch (e) {
            console.warn("Error destroying player:", e);
          }
        }

        // Determine what to load
        let videoSource;
        if (usingFallback && fallbackVideoId) {
          videoSource = { videoId: fallbackVideoId };
        } else if (youtubeChannelId) {
          // For live stream, we need to load it differently
          // YouTube doesn't allow direct channel live embeds via the API
          // We'll use a workaround with the iframe directly
          videoSource = null; // Will handle separately
        }

        if (videoSource) {
          playerRef.current = new window.YT.Player(iframeRef.current, {
            ...videoSource,
            host: "https://www.youtube-nocookie.com",
            playerVars: {
              autoplay: autoPlay ? 1 : 0,
              mute: 1,
              controls: 0,
              showinfo: 0,
              rel: 0,
              modestbranding: 1,
              playsinline: 1,
              iv_load_policy: 3,
              fs: 0,
              disablekb: 1,
              origin: window.location.origin,
              enablejsapi: 1,
              cc_load_policy: 0,
            },
            events: {
              onReady: (event) => {
                setPlayerReady(true);
                setIsLoading(false);
                setDuration(event.target.getDuration());
                if (autoPlay) {
                  event.target.playVideo();
                  setIsPlaying(true);
                }
                onReady?.();
              },
              onStateChange: (event) => {
                switch (event.data) {
                  case window.YT.PlayerState.PLAYING:
                    setIsPlaying(true);
                    setIsLoading(false);
                    break;
                  case window.YT.PlayerState.PAUSED:
                    setIsPlaying(false);
                    break;
                  case window.YT.PlayerState.BUFFERING:
                    setIsLoading(true);
                    break;
                  case window.YT.PlayerState.ENDED:
                    setIsPlaying(false);
                    break;
                  default:
                    break;
                }
              },
              onError: (event) => {
                console.error("[YouTubePush] Player error:", event.data);
                setIsLoading(false);
                if (!usingFallback && fallbackVideoId) {
                  switchToFallback();
                }
              },
            },
          });
        }
      }
    };

    if (window.YT && window.YT.Player) {
      // Small delay to ensure DOM is ready
      setTimeout(initPlayer, 100);
    } else {
      const existingCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        existingCallback?.();
        initPlayer();
      };
    }

    return () => {
      if (checkLiveTimeout.current) {
        clearTimeout(checkLiveTimeout.current);
      }
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.warn("Cleanup error:", e);
        }
        playerRef.current = null;
      }
    };
  }, [youtubeChannelId, fallbackVideoId, autoPlay, onReady, usingFallback, switchToFallback]);

  // Update current time periodically
  useEffect(() => {
    if (!playerReady) return;

    const interval = setInterval(() => {
      if (playerRef.current && typeof playerRef.current.getCurrentTime === "function") {
        setCurrentTime(playerRef.current.getCurrentTime());
        setDuration(playerRef.current.getDuration());
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [playerReady]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    if (isPlaying) {
      hideControlsTimeout.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  const handleMouseMove = () => resetControlsTimeout();
  const handleMouseLeave = () => {
    if (isPlaying) {
      setShowControls(false);
    }
  };

  // Controls
  const togglePlay = () => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    if (isMuted) {
      playerRef.current.unMute();
      playerRef.current.setVolume(volume);
      setIsMuted(false);
    } else {
      playerRef.current.mute();
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    if (playerRef.current) {
      playerRef.current.setVolume(newVolume);
      if (newVolume === 0) {
        playerRef.current.mute();
        setIsMuted(true);
      } else if (isMuted) {
        playerRef.current.unMute();
        setIsMuted(false);
      }
    }
  };

  const handleSeek = (e) => {
    if (!playerRef.current || !usingFallback) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const seekTime = percent * duration;
    playerRef.current.seekTo(seekTime, true);
    setCurrentTime(seekTime);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds) || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle live stream iframe - use native iframe for live stream
  if (!usingFallback && youtubeChannelId) {
    return (
      <div
        ref={containerRef}
        className="relative w-full aspect-video bg-black"
        data-testid="youtube-push-player-live"
      >
        {/* Live Stream Iframe with message listener for errors */}
        <iframe
          src={`https://www.youtube.com/embed/live_stream?channel=${youtubeChannelId}&autoplay=1&mute=1&modestbranding=1&rel=0&playsinline=1&enablejsapi=1`}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Live Stream"
          onError={() => {
            console.log("[YouTubePush] Iframe error, switching to fallback");
            if (fallbackVideoId) {
              switchToFallback();
            }
          }}
          onLoad={(e) => {
            setIsLoading(false);
            onReady?.();
            onLiveStatusChange?.(true);
            
            // Check if the iframe shows an error after a delay
            // YouTube will show "This video is unavailable" if not live
            checkLiveTimeout.current = setTimeout(() => {
              // We can't directly check iframe content due to CORS
              // But YouTube will redirect or show error
              // For now, we rely on the user experience
            }, 5000);
          }}
        />
        
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
            <Loader2 className="w-12 h-12 text-white animate-spin" />
          </div>
        )}
        
        {/* Live badge */}
        <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-xs font-black uppercase tracking-widest z-20 pointer-events-none">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          LIVE
        </div>
        
        {/* Fallback switch button - manual override */}
        {fallbackVideoId && (
          <button
            onClick={switchToFallback}
            className="absolute bottom-4 right-4 px-3 py-1.5 bg-zinc-800/80 hover:bg-zinc-700 text-white text-xs font-medium z-20 transition-colors"
            title="Not seeing the stream? Click to watch replay"
          >
            <Radio className="w-3 h-3 inline mr-1" />
            Watch Replay Instead
          </button>
        )}
      </div>
    );
  }

  // No content available at all
  if (!youtubeChannelId && !fallbackVideoId) {
    return (
      <div className="relative w-full aspect-video bg-black flex items-center justify-center">
        <div className="text-center">
          <Radio className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500">No video source configured</p>
        </div>
      </div>
    );
  }

  // Show fallback unavailable state if live stream failed and no fallback
  if (usingFallback && !fallbackVideoId) {
    return (
      <div className="relative w-full aspect-video bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <Radio className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-white text-xl font-bold mb-2">Channel Not Live</h3>
          <p className="text-zinc-500">This channel is not currently broadcasting</p>
        </div>
      </div>
    );
  }

  // Fallback player with full controls
  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video bg-black group"
      style={{ overflow: 'hidden' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-testid="youtube-push-player-fallback"
    >
      {/* YouTube Iframe Container */}
      <div 
        className="absolute overflow-hidden"
        style={{ 
          top: '-5%', 
          left: '-2%', 
          width: '104%', 
          height: '115%',
        }}
      >
        <div
          ref={iframeRef}
          className="w-full h-full"
        />
      </div>

      {/* Clickable overlay */}
      <div
        className="absolute inset-0 z-10"
        onClick={togglePlay}
        style={{ pointerEvents: playerReady ? "auto" : "none" }}
      />

      {/* Loading Spinner */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-25">
          <Loader2 className="w-12 h-12 text-white animate-spin" />
        </div>
      )}

      {/* Offline/Replay Badge */}
      <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-zinc-700 text-white text-xs font-bold uppercase tracking-widest z-30">
        <Radio className="w-3 h-3" />
        OFFLINE • PLAYING REPLAY
      </div>

      {/* Custom Controls Overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 z-20 ${
          showControls ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Center Play Button */}
        <button
          className="absolute inset-0 flex items-center justify-center"
          onClick={togglePlay}
        >
          {!isPlaying && !isLoading && (
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors">
              <Play className="w-10 h-10 text-white ml-1" fill="white" />
            </div>
          )}
        </button>

        {/* Bottom Controls Bar */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Progress Bar */}
          {duration > 0 && (
            <div
              className="relative h-1 bg-white/20 mb-4 cursor-pointer group/progress"
              onClick={(e) => {
                e.stopPropagation();
                handleSeek(e);
              }}
            >
              <div
                className="absolute h-full bg-orange-500"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-orange-500 rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity"
                style={{ left: `${(currentTime / duration) * 100}%` }}
              />
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            {/* Left Controls */}
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay();
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-white" />
                ) : (
                  <Play className="w-6 h-6 text-white" fill="white" />
                )}
              </button>

              <div className="flex items-center gap-2 group/volume">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMute();
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="w-5 h-5 text-white" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-white" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleVolumeChange(e);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-0 group-hover/volume:w-20 transition-all duration-200 accent-orange-500"
                />
              </div>

              <span className="text-white text-sm font-mono">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isFullscreen ? (
                  <Minimize className="w-5 h-5 text-white" />
                ) : (
                  <Maximize className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouTubePushPlayer;